import { useCallback } from "react";
import { useSyncExternalStore } from "react";
import {
  isReplaceAction,
  navReducer,
  parseNavState,
  serializeNavState,
  type NavAction,
  type SearchTab,
  type WorkspaceView,
} from "./nav-state.js";

/**
 * Facility-navigation URL state (FN / ADR-014; hardened per ADR-023) — the app's hand-rolled "router", no routing library.
 * The **query string IS the state**; five params, legacy-parity:
 * - `arc_fac_selected=<id>` — the drilled-into facility (browse). Absent ⇒ the root list.
 * - `arc_facility=<id>` — the facility whose MANUALS view is open.
 * - `arc_manual=<id>` — a manual open in the full-screen READER (a clean top-level route; takes precedence when set).
 * - `arc_edit=<id>` — a manual open in the full-screen AUTHORING editor (ADR-002 Phase 10; wins over the reader).
 * - `arc_search=<q>` — the global search view (FN4b); `arc_stab=<tab>` — its active tab.
 *
 * This hook is the thin REACT SHELL over the pure, testable core in {@link file://./nav-state.ts}: it reads the query via
 * `useSyncExternalStore` and dispatches semantic {@link NavAction}s through {@link navReducer} — the reducer owns all
 * precedence + param-clearing, so no writer hand-maintains `URLSearchParams` (the fragility a param addition used to
 * invite). Writers read the LIVE `location.search` at call time (not a closed-over snapshot), so they're stable and never
 * act on stale state.
 *
 * REACTIVITY (plan-panel #2): `history.pushState`/`replaceState` do NOT dispatch `popstate` (only user back/forward does),
 * so every write also fires a custom `arc:navchange` event, and the store subscribes to BOTH `popstate` and it.
 */
const NAV_EVENT = "arc:navchange";
/** `history.state` of a workspace entry pushed by `openView` (ADR-032) — its close pops it instead of replacing it. */
const WORKSPACE_ENTRY_STATE = { arcWorkspace: true } as const;
function isPushedWorkspaceEntry(state: unknown): boolean {
  return typeof state === "object" && state !== null && (state as { arcWorkspace?: unknown }).arcWorkspace === true;
}

function subscribe(cb: () => void): () => void {
  window.addEventListener("popstate", cb);
  window.addEventListener(NAV_EVENT, cb);
  return () => {
    window.removeEventListener("popstate", cb);
    window.removeEventListener(NAV_EVENT, cb);
  };
}
function getSnapshot(): string {
  return window.location.search;
}

export interface FacilityNavParams {
  /** `arc_fac_selected` — the drilled-into facility (null ⇒ root list). */
  selectedId: number | null;
  /** `arc_facility` — the facility whose manuals are open (null ⇒ not in the manuals view). */
  manualsId: number | null;
  /** `arc_manual` — a manual open in the full-screen reader (null ⇒ none). With `arc_facility` the manuals view consumes
   *  it; WITHOUT it the drill-down renders the reader standalone (a clean shareable `?arc_manual=<id>`). */
  openManualId: number | null;
  /** `arc_edit` — a manual open in the full-screen AUTHORING editor (null ⇒ none). The highest-precedence full-screen
   *  route: when set the drill-down renders the editor (a clean shareable `?arc_edit=<id>`), above the reader. */
  editManualId: number | null;
  /** `arc_details` — the ONE-SHOT details-drawer target over the manuals view (consumed by the view via `dropStale`). */
  detailsManualId: number | null;
  /** `arc_search` — the active global-search query (null ⇒ not in the search view). Trimmed; "" ⇒ null. */
  searchQuery: string | null;
  /** `arc_stab` — the active search tab (null ⇒ not set; the view derives a default). */
  searchTab: SearchTab | null;
  /** `arc_view` — the "Dashboard & settings" workspace page (ADR-032), a top-level route above the realm split. */
  view: WorkspaceView | null;
  /** `arc_role` — the role selected in Manage roles (ADR-030 Phase D); null ⇒ none. */
  roleId: number | null;
  /** Drill into a facility (or `null` = home/root list). Leaves the manuals + search views. Adds a history entry. */
  drillTo: (id: number | null) => void;
  /** Open a facility's manuals view. **Keeps** any active search + tab + drill selection underneath. `replace` (default
   *  false) collapses the current history entry — used by the reader's exit so browser Back can't re-open the reader. */
  openManuals: (id: number, replace?: boolean) => void;
  /** The manuals view of `facilityId` with `manualId`'s details drawer open (a linked-manual card's navigation). */
  openManualDetails: (facilityId: number, manualId: number, replace?: boolean) => void;
  /** Open a manual in the full-screen READER as a clean top-level view: sets `arc_manual`, clears `arc_facility`/
   *  `arc_search`/`arc_stab`. Also the ADR-020 linked-manual landing (the drawer's link arrow opens the target's reader). */
  viewManual: (manualId: number) => void;
  /** Open a manual in the full-screen AUTHORING editor (ADR-002 Phase 10) as a clean top-level view: sets `arc_edit`,
   *  clears `arc_manual`/`arc_facility`/`arc_search`/`arc_stab`. The Edit button in the manuals view/drawer calls this. */
  editManual: (manualId: number) => void;
  /** Set the active search tab (replaceState — no history entry per chip click). */
  setSearchTab: (tab: SearchTab) => void;
  /** Leave the manuals view, back to the drill level. */
  closeManuals: () => void;
  /** Open the global search for a query (leaves any open manuals view; keeps the drill selection underneath). */
  openSearch: (query: string) => void;
  /** Leave the search ("End search"), back to the drill level. */
  closeSearch: () => void;
  /** Drop a param WITHOUT a new history entry (stale-id cleanup after data resolves, or consuming the one-shot open). */
  dropStale: (key: "arc_fac_selected" | "arc_facility" | "arc_manual" | "arc_edit" | "arc_details" | "arc_role") => void;
  /** Open a workspace page. Keeps everything underneath. Pushes one entry; `replace` for a page switch INSIDE the
   *  workspace (one history entry for the whole visit). */
  openView: (view: WorkspaceView, replace?: boolean) => void;
  /** Leave the workspace (replace — strips only `arc_view`, so Back can never re-open it). */
  closeView: () => void;
  /** Select a role in Manage roles (replace — no history entry per click); null clears it. */
  selectRole: (id: number | null) => void;
}

export function useFacilityNavParams(): FacilityNavParams {
  const search = useSyncExternalStore(subscribe, getSnapshot, () => "");
  const state = parseNavState(search);

  // Apply a semantic action through the pure reducer, then commit to history + notify the store. Reads the LIVE query at
  // call time so it never acts on a stale render's snapshot.
  const dispatch = useCallback((action: NavAction) => {
    const current = window.location.search;
    // Closing a workspace entry WE pushed pops it: replacing it would leave a twin of its predecessor (open kept every
    // param, so the predecessor IS the restore target) and the next browser Back would be a dead press (panel). A
    // deep-linked visit has no pushed entry to pop and takes the replace-strip below.
    if (action.type === "closeView" && isPushedWorkspaceEntry(window.history.state)) {
      window.history.back(); // popstate re-renders the store
      return;
    }
    const next = navReducer(parseNavState(current), action);
    const search = serializeNavState(next, current);
    // An action that changes nothing writes nothing: no twin history entry (a dead Back press), no spurious event.
    if (search === current) return;
    const url = search || window.location.pathname; // clean root URL when empty
    // A page switch inside the workspace replaces but must KEEP the "pushed by us" mark; a pushed workspace entry is
    // marked so its close can pop it.
    if (isReplaceAction(action)) window.history.replaceState(window.history.state ?? null, "", url);
    else window.history.pushState(action.type === "openView" ? WORKSPACE_ENTRY_STATE : null, "", url);
    window.dispatchEvent(new Event(NAV_EVENT));
  }, []);

  const drillTo = useCallback((id: number | null) => dispatch({ type: "drillTo", id }), [dispatch]);
  const openManuals = useCallback((id: number, replace = false) => dispatch({ type: "openManuals", id, replace }), [dispatch]);
  /** A facility's manuals view with one manual's details drawer open — a linked-manual card's navigation. */
  const openManualDetails = useCallback(
    (facilityId: number, manualId: number, replace = false) => dispatch({ type: "openManualDetails", facilityId, manualId, replace }),
    [dispatch],
  );
  const viewManual = useCallback((manualId: number) => dispatch({ type: "viewManual", id: manualId }), [dispatch]);
  const editManual = useCallback((manualId: number) => dispatch({ type: "editManual", id: manualId }), [dispatch]);
  const closeManuals = useCallback(() => dispatch({ type: "closeManuals" }), [dispatch]);
  const openSearch = useCallback((query: string) => dispatch({ type: "openSearch", query }), [dispatch]);
  const closeSearch = useCallback(() => dispatch({ type: "closeSearch" }), [dispatch]);
  const setSearchTab = useCallback((tab: SearchTab) => dispatch({ type: "setSearchTab", tab }), [dispatch]);
  const dropStale = useCallback(
    (key: "arc_fac_selected" | "arc_facility" | "arc_manual" | "arc_edit" | "arc_details" | "arc_role") => dispatch({ type: "dropStale", key }),
    [dispatch],
  );
  const openView = useCallback((view: WorkspaceView, replace = false) => dispatch({ type: "openView", view, replace }), [dispatch]);
  const closeView = useCallback(() => dispatch({ type: "closeView" }), [dispatch]);
  const selectRole = useCallback((id: number | null) => dispatch({ type: "selectRole", id }), [dispatch]);

  return {
    selectedId: state.selectedId,
    manualsId: state.manualsId,
    openManualId: state.openManualId,
    editManualId: state.editManualId,
    detailsManualId: state.detailsManualId,
    searchQuery: state.searchQuery,
    searchTab: state.searchTab,
    view: state.view,
    roleId: state.roleId,
    drillTo,
    openManuals,
    openManualDetails,
    viewManual,
    editManual,
    closeManuals,
    openSearch,
    closeSearch,
    setSearchTab,
    dropStale,
    openView,
    closeView,
    selectRole,
  };
}
